import { useState, useCallback } from 'react'

// API endpoint for Pesapal Lambda
const PESAPAL_API_ENDPOINT = process.env.REACT_APP_PESAPAL_API_ENDPOINT || 
  'https://hqjmryaj16.execute-api.ap-southeast-1.amazonaws.com/dev/pesapal'

/**
 * Custom hook for Pesapal payment integration
 */
export const usePesapal = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Mint a short-lived signed price token (optional hardening).
   * If this endpoint is unavailable, callers can proceed without a token.
   */
  const mintPriceToken = useCallback(async (tokenRequest) => {
    try {
      const response = await fetch(`${PESAPAL_API_ENDPOINT}/mint-price-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tokenRequest)
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to mint price token')
      }

      return result
    } catch (err) {
      console.warn('Mint price token error:', err)
      throw err
    }
  }, [])

  /**
   * Submit a payment order to Pesapal
   * @param {Object} paymentData - Payment order data
   * @returns {Promise<Object>} - Pesapal response with redirect_url
   */
  const submitOrder = useCallback(async (paymentData) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${PESAPAL_API_ENDPOINT}/submit-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData)
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit payment order')
      }

      setLoading(false)
      return result
    } catch (err) {
      console.error('Submit order error:', err)
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [])

  /**
   * Check payment status
   * @param {string} orderTrackingId - Pesapal order tracking ID
   * @returns {Promise<Object>} - Payment status
   */
  const checkPaymentStatus = useCallback(async (orderTrackingId) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${PESAPAL_API_ENDPOINT}/payment-status?orderTrackingId=${orderTrackingId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to check payment status')
      }

      setLoading(false)
      return result
    } catch (err) {
      console.error('Check payment status error:', err)
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [])

  /**
   * Process subscription after successful payment
   * @param {string} orderTrackingId - Pesapal order tracking ID
   * @param {Object} subscriptionData - Subscription data (userId, packageType, duration)
   * @returns {Promise<Object>} - Subscription update result
   */
  const processSubscription = useCallback(async (orderTrackingId, subscriptionData) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${PESAPAL_API_ENDPOINT}/process-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderTrackingId,
          ...subscriptionData
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to process subscription')
      }

      setLoading(false)
      return result
    } catch (err) {
      console.error('Process subscription error:', err)
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [])

  return {
    mintPriceToken,
    submitOrder,
    checkPaymentStatus,
    processSubscription,
    loading,
    error
  }
}
